/**
 * FOOTER - CHATBOT IMÓVEIS®
 * =================================================================
 * Componente de rodapé (footer) para dashboard
 *
 * Funcionalidades:
 * - 3 colunas de navegação (Produto, Legal, Suporte)
 * - Links institucionais (Termos, Privacidade, LGPD, Status, Ajuda)
 * - Versão dinâmica + commit hash
 * - Responsivo (stack vertical mobile, colunas desktop)
 * - Dark mode com brand tokens
 * - Semantic HTML com role="contentinfo"
 *
 * Stack: React 18 + Tailwind + Wouter + Brand Tokens
 * A11y: WCAG 2.2 AA compliant (3:1 contrast, tab order, labels)
 * Data: 2025-11-09
 */

import React from 'react';
import { Link } from 'wouter';
import {
  Home,
  FileText,
  Shield,
  Activity,
  HelpCircle,
  Mail,
  ExternalLink,
  Zap,
} from 'lucide-react';

/* ========================================
 * TYPES & INTERFACES
 * ======================================== */

interface FooterLinkProps {
  href: string;
  label: string;
  external?: boolean;
  icon?: React.ElementType;
}

interface FooterColumnProps {
  title: string;
  links: FooterLinkProps[];
}

interface FooterProps {
  /** Classe CSS adicional */
  className?: string;
  /** Exibir versão e commit hash (default: true) */
  showVersion?: boolean;
}

/* ========================================
 * FOOTER DATA
 * ======================================== */

const footerColumns: FooterColumnProps[] = [
  {
    title: 'Produto',
    links: [
      { href: '/dashboard', label: 'Centro de Comando', icon: Home },
      { href: '/dashboard/pricing', label: 'Planos e Preços', icon: Zap },
      { href: '/dashboard/features', label: 'Funcionalidades', icon: Activity },
      { href: '/blog', label: 'Blog', external: true, icon: ExternalLink },
    ],
  },
  {
    title: 'Legal',
    links: [
      { href: '/termos', label: 'Termos de Uso', icon: FileText },
      { href: '/privacidade', label: 'Privacidade', icon: Shield },
      { href: '/lgpd', label: 'LGPD', icon: Shield },
      {
        href: 'https://status.chatbotimoveis.com.br',
        label: 'Status',
        external: true,
        icon: Activity,
      },
    ],
  },
  {
    title: 'Suporte',
    links: [
      { href: '/dashboard/help', label: 'Central de Ajuda', icon: HelpCircle },
      { href: '/dashboard/contact', label: 'Fale Conosco', icon: Mail },
      {
        href: 'https://api.whatsapp.com/send?phone=5511999999999',
        label: 'WhatsApp',
        external: true,
        icon: ExternalLink,
      },
    ],
  },
];

/* ========================================
 * COMPONENT
 * ======================================== */

/**
 * Componente Footer - Rodapé do dashboard
 *
 * Uso:
 * ```tsx
 * <Footer />
 * ```
 *
 * Ou com customização:
 * ```tsx
 * <Footer className="mt-auto" showVersion={true} />
 * ```
 */
export function Footer({ className = '', showVersion = true }: FooterProps) {
  // Versão e commit hash de variáveis de ambiente
  const APP_VERSION = import.meta.env.VITE_APP_VERSION || '3.0.0-beta';
  const GIT_COMMIT_HASH = import.meta.env.VITE_GIT_COMMIT_HASH
    ? import.meta.env.VITE_GIT_COMMIT_HASH.substring(0, 7)
    : 'dev';

  const currentYear = new Date().getFullYear();

  return (
    <footer
      role='contentinfo'
      className={`w-full border-t border-gray-200 dark:border-gray-800
                 bg-white dark:bg-gray-900
                 transition-colors duration-brand-fast
                 ${className}`}
      data-testid='dashboard-footer'
      aria-label='Rodapé do dashboard'
    >
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        {/* ========================================
         * FOOTER COLUMNS (3 COLUNAS)
         * ======================================== */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-8 py-12'>
          {footerColumns.map(column => (
            <div key={column.title} className='space-y-4'>
              {/* Column Title */}
              <h3
                className='text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider'
                id={`footer-${column.title.toLowerCase()}`}
              >
                {column.title}
              </h3>

              {/* Column Links */}
              <ul className='space-y-3' aria-labelledby={`footer-${column.title.toLowerCase()}`}>
                {column.links.map(link => {
                  const Icon = link.icon;
                  const linkContent = (
                    <>
                      {Icon && <Icon className='w-4 h-4 flex-shrink-0' aria-hidden='true' />}
                      <span className='flex-1'>{link.label}</span>
                      {link.external && (
                        <ExternalLink
                          className='w-3 h-3 text-gray-400 dark:text-gray-500'
                          aria-label='(abre em nova aba)'
                        />
                      )}
                    </>
                  );

                  const linkClasses = `flex items-center gap-2 text-sm
                                      text-gray-600 dark:text-gray-400
                                      hover:text-brand-primary dark:hover:text-brand-primary-hover
                                      focus-brand
                                      transition-colors duration-brand-fast
                                      group`;

                  // External link
                  if (link.external) {
                    return (
                      <li key={link.href}>
                        <a
                          href={link.href}
                          target='_blank'
                          rel='noopener noreferrer'
                          className={linkClasses}
                          data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {linkContent}
                        </a>
                      </li>
                    );
                  }

                  // Internal link (Wouter)
                  return (
                    <li key={link.href}>
                      <Link href={link.href}>
                        <a
                          className={linkClasses}
                          data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {linkContent}
                        </a>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* ========================================
         * FOOTER BOTTOM (COPYRIGHT + VERSION)
         * ======================================== */}
        <div
          className='border-t border-gray-200 dark:border-gray-800 py-6
                     flex flex-col sm:flex-row items-center justify-between gap-4'
        >
          {/* Copyright */}
          <p className='text-xs text-gray-500 dark:text-gray-400 text-center sm:text-left'>
            © {currentYear}{' '}
            <span className='font-semibold text-gray-700 dark:text-gray-300'>
              ChatbotImóveis<sup className='font-normal'>®</sup>
            </span>
            {' · '}
            <span className='text-gray-400 dark:text-gray-500'>Todos os direitos reservados</span>
          </p>

          {/* Version Info */}
          {showVersion && (
            <div
              className='flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400'
              aria-label={`Versão ${APP_VERSION}, commit ${GIT_COMMIT_HASH}`}
            >
              <Zap className='w-3 h-3 text-brand-accent' aria-hidden='true' />
              <span data-testid='footer-version'>v{APP_VERSION}</span>
              <span className='text-gray-300 dark:text-gray-700' aria-hidden='true'>
                ·
              </span>
              <code
                className='px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-[0.65rem] font-mono'
                data-testid='footer-commit'
              >
                {GIT_COMMIT_HASH}
              </code>
            </div>
          )}
        </div>

        {/* ========================================
         * ACCESSIBILITY NOTE (SCREEN READERS ONLY)
         * ======================================== */}
        <div className='sr-only' aria-live='polite'>
          Rodapé do dashboard com links para Produto, Legal e Suporte. Versão {APP_VERSION}, commit{' '}
          {GIT_COMMIT_HASH}.
        </div>
      </div>
    </footer>
  );
}
